import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { WizardLayout } from '../components/WizardLayout';
import { CompanyInfoStep } from '../steps/CompanyInfoStep';
import { ContactStep } from '../steps/ContactStep';
import { SkillsStep } from '../steps/SkillsStep';
import { LicensesStep } from '../steps/LicensesStep';
import { InsuranceStep } from '../steps/InsuranceStep';
import { TermsStep } from '../steps/TermsStep';
import { W9Step } from '../steps/W9Step';
import { AchStep } from '../steps/AchStep';
import { ReviewStep } from '../steps/ReviewStep';
import { submitOnboarding } from '../api';
import {
  REQUEST_REASON_MAX_CHARS,
  REQUEST_REASON_MIN_CHARS,
  RequestedTermsSchema,
  validateRequestReason,
  isNegotiatedPaymentTermsDays,
  type PaymentTermsDays,
} from '@hts/shared';
import {
  INITIAL_FORM_DATA,
  activeStepTitles,
  stepEditIndex,
  type FormData,
} from '../types';

interface OnboardingFormProps {
  verifiedEmail: string;
  /**
   * MUL-3919 §2: resolved server-side at /verify/check from the verified email
   * via ConnectWise contact lookup. When false the wizard hides the
   * "Agreement Terms" step (eight steps, not nine) and never sends
   * `requestedTerms` or `requestReason` in the submit payload.
   */
  paymentTermsNegotiable: boolean;
}

// Canonical step indexes (always nine) — kept so ReviewStep's "Edit" buttons
// can target a stable identifier regardless of how many steps the user sees.
const STEP_COMPANY = 0;
const STEP_CONTACT = 1;
const STEP_SKILLS = 2;
const STEP_LICENSES = 3;
const STEP_INSURANCE = 4;
const STEP_TERMS = 5;
const STEP_W9 = 6;
const STEP_ACH = 7;
const STEP_REVIEW = 8;

export function OnboardingForm({ verifiedEmail, paymentTermsNegotiable }: OnboardingFormProps) {
  const navigate = useNavigate();
  const stepTitles = activeStepTitles(paymentTermsNegotiable);
  const [step, setStep] = useState(0);
  const [data, setData] = useState<FormData>({
    ...INITIAL_FORM_DATA,
    contactEmail: verifiedEmail,
  });
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);

  // Persist the eligibility flag and verified email in sessionStorage so a
  // page refresh (or returning later via the status URL) doesn't re-render
  // the form for a sub who shouldn't even see this step. The verified email
  // is already stored elsewhere in the auth flow; we mirror it for clarity.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        'hts_payment_terms_negotiable',
        paymentTermsNegotiable ? 'true' : 'false',
      );
    } catch {
      // sessionStorage can throw in private mode; silently ignore — the form
      // still works, the worst case is one extra step on reload.
    }
  }, [paymentTermsNegotiable]);

  // Reset the local form if the verified email changes (e.g. after a fresh
  // /verify/check). Otherwise we carry stale data into a new submission.
  useEffect(() => {
    setData((prev) => (prev.contactEmail === verifiedEmail ? prev : { ...INITIAL_FORM_DATA, contactEmail: verifiedEmail }));
    setStep(0);
    setFieldErrors({});
    setShowErrors(false);
    setError(null);
    setAgreed(false);
  }, [verifiedEmail]);

  const update = (updates: Partial<FormData>) => {
    setData((prev) => ({ ...prev, ...updates }));
    // Clear field errors for changed fields
    const clearedErrors = { ...fieldErrors };
    for (const key of Object.keys(updates)) {
      delete clearedErrors[key];
    }
    setFieldErrors(clearedErrors);
  };

  // Map active step index (0..stepTitles.length-1) to canonical step index (0..8).
  // When the terms step is hidden, active index 5 maps to canonical W-9 (6).
  const activeToCanonical = (activeIndex: number): number => {
    if (paymentTermsNegotiable) return activeIndex;
    if (activeIndex < STEP_TERMS) return activeIndex;
    return activeIndex + 1;
  };

  const getStepErrors = (): Record<string, string> => {
    const canonical = activeToCanonical(step);
    const errors: Record<string, string> = {};
    switch (canonical) {
      case STEP_COMPANY:
        if (!data.companyName) errors.companyName = 'Please enter your company name';
        if (!data.addressLine1) errors.addressLine1 = 'Please enter your street address';
        if (!data.city) errors.city = 'Please enter your city';
        if (!data.state) errors.state = 'Please choose your state';
        if (!data.zip) errors.zip = 'Please enter your ZIP code';
        else if (data.zip.length < 5) errors.zip = 'ZIP code must be at least 5 characters';
        if (!data.phone) errors.phone = 'Please enter your phone number';
        else if (data.phone.replace(/\D/g, '').length < 10) errors.phone = 'Please enter a full 10-digit phone number';
        if (!data.einTaxId) errors.einTaxId = 'Please enter your EIN / Tax ID';
        else if (data.einTaxId.replace(/\D/g, '').length < 9) errors.einTaxId = 'EIN must be 9 digits (example: 12-3456789)';
        break;
      case STEP_CONTACT:
        if (!data.contactFirstName) errors.contactFirstName = 'Please enter the contact\'s first name';
        if (!data.contactLastName) errors.contactLastName = 'Please enter the contact\'s last name';
        if (!data.contactEmail) errors.contactEmail = 'Please enter an email address';
        if (!data.contactPhone) errors.contactPhone = 'Please enter a phone number';
        else if (data.contactPhone.replace(/\D/g, '').length < 10) errors.contactPhone = 'Please enter a full 10-digit phone number';
        if (data.hasAlternativeSigner) {
          if (!data.signerFirstName) errors.signerFirstName = 'Please enter the signer\'s first name';
          if (!data.signerLastName) errors.signerLastName = 'Please enter the signer\'s last name';
          if (!data.signerEmail) errors.signerEmail = 'Please enter the signer\'s email address';
          else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.signerEmail)) errors.signerEmail = 'Please enter a valid email address';
        }
        break;
      case STEP_SKILLS:
        if (data.skills.length === 0) errors.skills = 'Please select at least one service your company provides';
        break;
      case STEP_INSURANCE:
        if (!data.coiFile) errors.coiFile = 'Please upload your Certificate of Insurance';
        if (!data.coiAnalysisToken) errors.coiFile = 'Please wait for the certificate to be read';
        if (!data.coiConfirmed) errors.coiConfirmed = 'Please confirm the certificate results';
        if (data.coiParserStatus !== 'failed') {
          if (!data.glExpiresAt) errors.glExpiresAt = 'Upload a certificate with a readable GL date';
          if (!data.autoExpiresAt) errors.autoExpiresAt = 'Upload a certificate with a readable auto date';
          if (!data.wcExpiresAt) errors.wcExpiresAt = 'Upload a certificate with a readable WC date';
          if (!data.umbrellaExpiresAt) errors.umbrellaExpiresAt = 'Upload a certificate with a readable umbrella date';
          if (Object.keys(data.coiLimits).length < 5) {
            errors.coiLimits = 'Upload a certificate with readable coverage limits';
          }
        }
        break;
      case STEP_TERMS: {
        const termsResult = RequestedTermsSchema.safeParse(data.requestedTerms);
        if (!termsResult.success) {
          errors.paymentTermsDays = 'Please select a valid payment term';
        }
        // MUL-3919 §6.1: a reason is required when the sub picks Net 30, Net 45 or Net 60.
        const requestedDays: PaymentTermsDays = data.requestedTerms?.paymentTermsDays ?? null;
        if (isNegotiatedPaymentTermsDays(requestedDays)) {
          const reasonCheck = validateRequestReason(data.requestReason ?? '');
          if (!reasonCheck.ok && reasonCheck.message) {
            errors.requestReason = reasonCheck.message;
          }
        }
        break;
      }
      case STEP_W9:
        if (!data.w9File) errors.w9File = 'Please upload your completed W-9 form';
        break;
      case STEP_ACH: {
        if (data.paymentMethod !== 'ach') return errors;
        if (!data.achVendorName) errors.achVendorName = 'Please enter the name on your bank account';
        if (!data.achAddressLine1) errors.achAddressLine1 = 'Please enter your address';
        if (!data.achCity) errors.achCity = 'Please enter your city';
        if (!data.achState) errors.achState = 'Please choose your state';
        if (!data.achZip) errors.achZip = 'Please enter your ZIP code';
        else if (data.achZip.length < 5) errors.achZip = 'ZIP code must be at least 5 characters';
        if (!data.achBankName) errors.achBankName = 'Please enter your bank name';
        if (data.achRoutingNumber.length !== 9) errors.achRoutingNumber = 'Routing number must be exactly 9 digits';
        if (data.achAccountNumber.length < 4) errors.achAccountNumber = 'Please enter your account number (at least 4 digits)';
        if (!data.bankVerifyFile) errors.bankVerifyFile = 'Please upload a cancelled check or bank verification letter';
        if (!data.achAccountType) errors.achAccountType = 'Please select an account type';
        break;
      }
      case STEP_REVIEW:
        if (!agreed) errors.agreed = 'Please check the box to confirm your information is correct';
        break;
    }
    return errors;
  };

  const validateStep = (): boolean => {
    return Object.keys(getStepErrors()).length === 0;
  };

  const next = () => {
    const errors = getStepErrors();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setShowErrors(true);
      // Scroll to first error
      setTimeout(() => {
        const firstError = document.querySelector('.error-text');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }
    setFieldErrors({});
    setShowErrors(false);
    if (step < stepTitles.length - 1) {
      setStep(step + 1);
      window.scrollTo(0, 0);
    }
  };

  const prev = () => {
    setFieldErrors({});
    setShowErrors(false);
    if (step > 0) {
      setStep(step - 1);
      window.scrollTo(0, 0);
    }
  };

  // Map a canonical step index (0..8) to the active index the wizard is on.
  // Called by ReviewStep's "Edit" buttons; respects the hidden step.
  const goToStep = (canonicalStepIndex: number) => {
    setFieldErrors({});
    setShowErrors(false);
    const target = paymentTermsNegotiable
      ? canonicalStepIndex
      : canonicalStepIndex <= STEP_TERMS
        ? canonicalStepIndex
        : canonicalStepIndex - 1;
    setStep(Math.max(0, Math.min(stepTitles.length - 1, target)));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!agreed || submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const formData = new globalThis.FormData();

      // Prepare the JSON data (excludes files and ACH raw data is included for server-side PDF)
      const isAch = data.paymentMethod === 'ach';
      const requestedDays: PaymentTermsDays = data.requestedTerms?.paymentTermsDays ?? null;

      // MUL-3919 §3.1: when the sub isn't eligible, `requestedTerms` and
      // `requestReason` are NEVER sent — the server drops them anyway. The
      // explicit null in the JSON keeps the schema happy (PaymentTermsDays
      // includes null) and prevents stale drafts from sneaking through.
      const jsonData: Record<string, unknown> = {
        companyName: data.companyName,
        addressLine1: data.addressLine1,
        addressLine2: data.addressLine2 || undefined,
        city: data.city,
        state: data.state,
        zip: data.zip,
        phone: data.phone,
        website: data.website || undefined,
        einTaxId: data.einTaxId,
        contactFirstName: data.contactFirstName,
        contactLastName: data.contactLastName,
        contactEmail: data.contactEmail,
        contactPhone: data.contactPhone,
        contactTitle: data.contactTitle || undefined,
        hasAlternativeSigner: data.hasAlternativeSigner,
        signerFirstName: data.hasAlternativeSigner ? data.signerFirstName : undefined,
        signerLastName: data.hasAlternativeSigner ? data.signerLastName : undefined,
        signerEmail: data.hasAlternativeSigner ? data.signerEmail : undefined,
        signerTitle: data.hasAlternativeSigner ? (data.signerTitle || undefined) : undefined,
        skills: data.skills,
        serviceRadiusMi: data.serviceRadiusMi,
        isNationalBroker: data.isNationalBroker,
        licenses: data.licenses.length > 0 ? data.licenses : undefined,
        glExpiresAt: data.glExpiresAt || undefined,
        autoExpiresAt: data.autoExpiresAt || undefined,
        wcExpiresAt: data.wcExpiresAt || undefined,
        umbrellaExpiresAt: data.umbrellaExpiresAt || undefined,
        coiAnalysisToken: data.coiAnalysisToken,
        coiConfirmed: data.coiConfirmed,
        paymentMethod: data.paymentMethod,
        achVendorName: isAch ? data.achVendorName : undefined,
        achAddressLine1: isAch ? data.achAddressLine1 : undefined,
        achAddressLine2: isAch ? (data.achAddressLine2 || undefined) : undefined,
        achCity: isAch ? data.achCity : undefined,
        achState: isAch ? data.achState : undefined,
        achZip: isAch ? data.achZip : undefined,
        achBankName: isAch ? data.achBankName : undefined,
        achRoutingNumber: isAch ? data.achRoutingNumber : undefined,
        achAccountNumber: isAch ? data.achAccountNumber : undefined,
        achAccountType: isAch ? data.achAccountType : undefined,
      };

      if (paymentTermsNegotiable) {
        if (isNegotiatedPaymentTermsDays(requestedDays)) {
          // Server re-validates; trim here to keep payloads tidy.
          const reason = (data.requestReason ?? '').trim();
          if (reason.length < REQUEST_REASON_MIN_CHARS || reason.length > REQUEST_REASON_MAX_CHARS) {
            throw new Error('Please complete the "Why are you requesting different terms?" field before submitting.');
          }
          jsonData.requestedTerms = { paymentTermsDays: requestedDays };
          jsonData.requestReason = reason;
        } else {
          // Standard path — explicit null keeps the contract shape stable.
          jsonData.requestedTerms = { paymentTermsDays: null };
        }
      }
      // When paymentTermsNegotiable is false we deliberately omit
      // requestedTerms + requestReason; the server falls back to the
      // standard path. (Existing legacy: setting requestedTerms to
      // { paymentTermsDays: null } is also acceptable; both behave identically.)

      formData.append('data', JSON.stringify(jsonData));

      if (data.w9File) formData.append('w9File', data.w9File);
      if (isAch && data.bankVerifyFile) formData.append('bankVerifyFile', data.bankVerifyFile);

      const result = await submitOnboarding(formData);
      // Carry agreementState forward to the thank-you page so the page can
      // render the right copy without re-fetching.
      const agreementState =
        result && typeof result === 'object' && 'agreementState' in result
          ? (result as { agreementState?: string }).agreementState
          : undefined;
      const query = new URLSearchParams({ token: result.accessToken });
      if (typeof agreementState === 'string') {
        query.set('agreementState', agreementState);
      }
      navigate(`/thank-you?${query.toString()}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(message);
      setSubmitting(false);
      window.scrollTo(0, 0);
    }
  };

  const errors = showErrors ? fieldErrors : {};
  const canonicalStep = activeToCanonical(step);

  const renderStep = () => {
    switch (canonicalStep) {
      case STEP_COMPANY: return <CompanyInfoStep data={data} onChange={update} errors={errors} />;
      case STEP_CONTACT: return <ContactStep data={data} onChange={update} readOnlyEmail errors={errors} />;
      case STEP_SKILLS: return <SkillsStep data={data} onChange={update} errors={errors} />;
      case STEP_LICENSES: return <LicensesStep data={data} onChange={update} />;
      case STEP_INSURANCE: return <InsuranceStep data={data} onChange={update} errors={errors} />;
      case STEP_TERMS: return (
        <TermsStep
          data={data}
          onChange={update}
          errors={errors}
          paymentTermsNegotiable={paymentTermsNegotiable}
        />
      );
      case STEP_W9: return <W9Step data={data} onChange={update} errors={errors} />;
      case STEP_ACH: return <AchStep data={data} onChange={update} errors={errors} />;
      case STEP_REVIEW:
        return (
          <ReviewStep
            data={data}
            onEditStep={goToStep}
            paymentTermsNegotiable={paymentTermsNegotiable}
          />
        );
      default: return null;
    }
  };

  return (
    <WizardLayout currentStep={step} totalSteps={stepTitles.length} stepTitle={stepTitles[step]}>
      <div className="card">
        {error && (
          <div className="alert alert-error" style={{ marginBottom: 16, whiteSpace: 'pre-line' }}>
            <strong>There was a problem:</strong>{' '}
            {error}
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleSubmit}
                disabled={submitting}
              >
                Try again
              </button>
            </div>
          </div>
        )}

        {showErrors && Object.keys(fieldErrors).length > 0 && (
          <div className="alert alert-error" style={{ marginBottom: 16 }}>
            Please fix the highlighted fields below before continuing.
          </div>
        )}

        {renderStep()}

        {canonicalStep === STEP_REVIEW && (
          <div style={{ marginTop: 24 }}>
            <label className="agreement-label">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <span>
                I confirm that all information provided above is correct and complete.{' '}
                {data.hasAlternativeSigner
                  ? `${data.signerFirstName || 'The person named above'} ${data.signerLastName || ''} is authorized to sign contracts and agreements on behalf of my company, and I have their permission to submit this information on their behalf.`.replace(/\s+/g, ' ').trim()
                  : 'I am authorized to sign contracts and agreements on behalf of my company.'}
                {data.paymentMethod === 'ach' && ' I authorize Harris Technology Services to verify this information and to send payments to the bank account listed above.'}
              </span>
            </label>
            {showErrors && errors.agreed && (
              <p className="error-text" style={{ marginLeft: 28 }}>{errors.agreed}</p>
            )}
          </div>
        )}

        <div className="step-actions">
          {step > 0 ? (
            <button type="button" className="btn btn-secondary" onClick={prev}>
              &#8592; Back
            </button>
          ) : (
            <div />
          )}

          {step < stepTitles.length - 1 ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={next}
            >
              Continue &#8594;
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-submit"
              onClick={handleSubmit}
              disabled={!validateStep() || submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner" /> Submitting Your Application...
                </>
              ) : (
                'Submit Application'
              )}
            </button>
          )}
        </div>
      </div>
    </WizardLayout>
  );
}

// Re-export the helper so other modules don't need a second import.
export { stepEditIndex };